import axios from 'axios'

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'

/**
 * Get user memories with pagination and filtering
 * @param {string} token - The authentication token
 * @param {object} params - Query parameters
 * @param {number} params.limit - Number of items per page (default: 50)
 * @param {number} params.skip - Number of items to skip (default: 0)
 * @param {string} params.status_filter - Filter by status (default: 'active')
 * @returns {Promise<object>} - { items: Memory[], total: number }
 */
export const getMemories = async (token, params = {}) => {
  const { limit = 50, skip = 0, status_filter = 'active' } = params

  try {
    const response = await axios.get(`${API_BASE_URL}/memory/me/memories`, {
      params: {
        limit,
        skip,
        status_filter,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    return response.data
  } catch (error) {
    console.error('Error fetching memories:', error)
    console.error('Error details:', error.response?.data)
    console.error('Error status:', error.response?.status)
    throw error
  }
}

/**
 * Create a new memory
 * @param {string} token - The authentication token
 * @param {object} memoryData - Memory data
 * @param {string} memoryData.content - Memory content (max 2000 chars)
 * @param {string} memoryData.category - Category: preference, instruction, fact, summary
 * @param {string} memoryData.importance - Importance: low, medium, high
 * @param {string[]} memoryData.tags - Optional tags
 * @returns {Promise<object>} - Created memory object
 */
export const createMemory = async (token, memoryData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/memory/me/memories`,
      memoryData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    )
    return response.data
  } catch (error) {
    console.error('Error creating memory:', error)
    throw error
  }
}

/**
 * Update an existing memory
 * @param {string} token - The authentication token
 * @param {string} memoryId - Memory ID
 * @param {object} memoryData - Updated memory data
 * @returns {Promise<object>} - Updated memory object
 */
export const updateMemory = async (token, memoryId, memoryData) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/memory/me/memories/${memoryId}`,
      memoryData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    )
    return response.data
  } catch (error) {
    console.error('Error updating memory:', error)
    throw error
  }
}

/**
 * Archive a memory (soft delete - removes from vector store)
 * @param {string} token - The authentication token
 * @param {string} memoryId - Memory ID
 * @returns {Promise<object>} - Archived memory object
 */
export const archiveMemory = async (token, memoryId) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/memory/me/memories/${memoryId}/archive`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    return response.data
  } catch (error) {
    console.error('Error archiving memory:', error)
    throw error
  }
}

/**
 * Delete a memory permanently
 * @param {string} token - The authentication token
 * @param {string} memoryId - Memory ID
 * @returns {Promise<void>}
 */
export const deleteMemory = async (token, memoryId) => {
  try {
    await axios.delete(`${API_BASE_URL}/memory/me/memories/${memoryId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  } catch (error) {
    console.error('Error deleting memory:', error)
    throw error
  }
}

// Constants for dropdowns
export const MEMORY_CATEGORIES = [
  { value: 'preference', label: 'Preferenza' },
  { value: 'instruction', label: 'Istruzione' },
  { value: 'fact', label: 'Fatto' },
  { value: 'summary', label: 'Riepilogo' },
]

export const MEMORY_IMPORTANCE = [
  { value: 'low', label: 'Bassa' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' },
]

export const MEMORY_STATUS = [
  { value: 'active', label: 'Attive' },
  { value: 'archived', label: 'Archiviate' },
]
